import { z } from "zod";

/**
 * The org accounts this browser is signed in to, for the switcher in the
 * organization menu.
 *
 * A person who belongs to two organizations holds two SEPARATE accounts —
 * `users.org_id` is a single non-null FK under `UNIQUE (email, org_id)` — each
 * with its own passkeys, SSO logins and sessions. So switching is not a server
 * operation: every entry here holds a session token that was minted by a real
 * sign-in to exactly that account, and switching only changes which of them is
 * the active `authToken`.
 *
 * That is deliberate. A server-side "same email in the other org" switch would
 * let any org admin who can set a member's email (SCIM, member edit) mint a
 * session in someone else's tenant. Holding one independently proven token per
 * account adds no trust relationship between tenants: each org's login policy,
 * deactivation and session expiry keep applying to its own account unchanged.
 *
 * The tokens sit in `localStorage` next to `authToken`, which is the same
 * storage and the same exposure the active session already has.
 */

const STORAGE_KEY = "rememberedAccounts";

const rememberedAccountSchema = z.object({
	userId: z.string(),
	orgId: z.string(),
	orgName: z.string(),
	email: z.string(),
	token: z.string(),
	sessionExpiresAt: z.string().nullable(),
});

export type RememberedAccount = z.infer<typeof rememberedAccountSchema>;

/**
 * Parse the stored list. Anything malformed — a hand-edited value, an older
 * shape — is dropped entry by entry rather than failing the whole list: the
 * worst outcome of losing an entry is one extra sign-in.
 */
export function parseRememberedAccounts(
	raw: string | null,
): RememberedAccount[] {
	if (raw === null) {
		return [];
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return [];
	}
	if (!Array.isArray(parsed)) {
		return [];
	}
	return parsed.flatMap((entry) => {
		const result = rememberedAccountSchema.safeParse(entry);
		return result.success ? [result.data] : [];
	});
}

/** Insert or replace by `userId`; one account never appears twice. */
export function withAccount(
	accounts: RememberedAccount[],
	account: RememberedAccount,
): RememberedAccount[] {
	return [...accounts.filter((a) => a.userId !== account.userId), account];
}

/**
 * Whether the account's session has passed its hard cutoff. Sessions are capped
 * server-side at 24h with no renewal, so an expired entry can only be revived by
 * signing in to that account again.
 */
export function isAccountExpired(
	account: RememberedAccount,
	now: number = Date.now(),
): boolean {
	if (account.sessionExpiresAt === null) {
		return false;
	}
	return new Date(account.sessionExpiresAt).getTime() <= now;
}

// The storage wrappers below use bare `catch {}` because `localStorage` throws
// on access in a handful of real configurations (Safari private mode,
// storage-partitioned embeds). A browser that cannot persist the list still
// works with the active session; it just has nothing to switch to.

export function listRememberedAccounts(): RememberedAccount[] {
	try {
		return parseRememberedAccounts(localStorage.getItem(STORAGE_KEY));
	} catch {
		return [];
	}
}

function writeRememberedAccounts(accounts: RememberedAccount[]): void {
	try {
		if (accounts.length === 0) {
			localStorage.removeItem(STORAGE_KEY);
		} else {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
		}
	} catch {
		// Nothing to do: the active session does not depend on this list.
	}
}

export function rememberAccount(account: RememberedAccount): void {
	writeRememberedAccounts(withAccount(listRememberedAccounts(), account));
}

export function forgetAccount(userId: string): void {
	writeRememberedAccounts(
		listRememberedAccounts().filter((a) => a.userId !== userId),
	);
}

/** Drop the account a dead token belongs to (a 401, the hard expiry). */
export function forgetAccountByToken(token: string): void {
	writeRememberedAccounts(
		listRememberedAccounts().filter((a) => a.token !== token),
	);
}

export function forgetAllAccounts(): void {
	writeRememberedAccounts([]);
}
